export class SupabaseClient {
  constructor(supabaseUrl, supabaseAnonKey, branch, repo) {
    this.supabaseUrl = supabaseUrl;
    this.supabaseAnonKey = supabaseAnonKey;
    this.branch = branch;
    this.repo = repo;
  }
  async fetchDb(uri, method, body = null) {
    try {
      const response = await fetch(`${this.supabaseUrl}${uri}`, {
        method: method || 'POST',
        headers: {
          apikey: this.supabaseAnonKey,
          Authorization: 'Bearer ' + this.supabaseAnonKey,
          'Content-Type': 'application/json'
        },
        body: body ? JSON.stringify(body) : undefined
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Supabase error:', error);
        throw new Error(`Supabase request failed: ${error.message || response.statusText}`);
      }
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (error) {
      console.error('Failed to fetch from Supabase:', error);
      throw error;
    }
  }
  async fetchDbPaginated(uri, batchSize = 500) {
    const allResults = [];
    let rangeStart = 0;
    let hasMore = true;
    while (hasMore) {
      const rangeEnd = rangeStart + batchSize - 1;
      try {
        const response = await fetch(`${this.supabaseUrl}${uri}`, {
          method: 'GET',
          headers: {
            apikey: this.supabaseAnonKey,
            Authorization: 'Bearer ' + this.supabaseAnonKey,
            'Content-Type': 'application/json',
            Range: `${rangeStart}-${rangeEnd}`
          }
        });
        if (!response.ok) {
          const error = await response.json();
          console.error('Supabase error:', error);
          throw new Error(`Supabase request failed: ${error.message || response.statusText}`);
        }
        const text = await response.text();
        const results = text ? JSON.parse(text) : [];
        allResults.push(...results);

        // Check if we got fewer results than requested, meaning we're done
        hasMore = results.length === batchSize;
        rangeStart += batchSize;
      } catch (error) {
        console.error('Failed to fetch from Supabase:', error);
        throw error;
      }
    }

    // return [];

    return allResults;
  }
  async fetchEntries(collection, searchTerm) {
    console.log('Fetching entries from supabase for collection:', collection);
    const searchQuery = searchTerm ? `&file_data=ilike.%25${encodeURIComponent(searchTerm)}%25` : '';
    const response = await this.fetchDbPaginated(`?repo=eq.${encodeURIComponent(this.repo)}&branch=eq.${encodeURIComponent(this.branch)}&collection=eq.${encodeURIComponent(collection)}${searchQuery}`);
    return response.map(data => ({
      file: data.file_meta,
      data: data.file_data
    }));
  }
  async fetchDbFiles(collection) {
    console.log('Fetching file list from supabase for collection:', collection);
    const response = await this.fetchDbPaginated(`?repo=eq.${encodeURIComponent(this.repo)}&branch=eq.${encodeURIComponent(this.branch)}&collection=eq.${encodeURIComponent(collection)}&select=file_id`);
    return response;
  }
  async removeDbFiles(collection, fileIds) {
    console.log('Removing files from supabase with IDs:', fileIds);
    const fileIdsParam = fileIds.map(id => `"${id}"`).join(',');
    await this.fetchDb(`?repo=eq.${encodeURIComponent(this.repo)}&branch=eq.${encodeURIComponent(this.branch)}&collection=eq.${encodeURIComponent(collection)}&file_id=in.(${fileIdsParam})`, 'DELETE');
  }
  async insertDbFile(collection, fileId, filePath, fileMeta, fileData) {
    await this.fetchDb('', 'POST', {
      repo: this.repo,
      branch: this.branch,
      collection: collection,
      file_id: fileId,
      file_path: filePath,
      file_meta: fileMeta,
      file_data: fileData
    });
  }
  async insertDbFilesBatch(files) {
    const batch = files.map(file => ({
      repo: this.repo,
      branch: this.branch,
      collection: file.collection,
      file_id: file.fileId,
      file_path: file.filePath,
      file_meta: file.fileMeta,
      file_data: file.fileData
    }));

    // Deduplicate batch to avoid "cannot affect row a second time" error
    const seen = new Map();
    const deduplicatedBatch = [];
    for (const item of batch) {
      const key = `${item.repo}|${item.branch}|${item.collection}|${item.file_id}`;
      if (!seen.has(key)) {
        seen.set(key, true);
        deduplicatedBatch.push(item);
      }
    }
    try {
      const response = await fetch(`${this.supabaseUrl}?on_conflict=repo,branch,collection,file_id`, {
        method: 'POST',
        headers: {
          apikey: this.supabaseAnonKey,
          Authorization: 'Bearer ' + this.supabaseAnonKey,
          'Content-Type': 'application/json',
          Prefer: 'resolution=merge-duplicates'
        },
        body: JSON.stringify(deduplicatedBatch)
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Supabase error:', error);
        throw new Error(`Supabase request failed: ${error.message || response.statusText}`);
      }
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (error) {
      console.error('Failed to insert batch to Supabase:', error);
      throw error;
    }
  }
  async validateFiles(collection, files, readFile, readFileMetadata) {
    let cacheOk = true;
    const dbFiles = await this.fetchDbFiles(collection);
    const fileIds = files.map(f => f.id);
    const dbFileIds = dbFiles.map(f => f.file_id);
    const filesToRemove = dbFileIds.filter(id => !fileIds.includes(id));
    if (filesToRemove.length > 0) {
      cacheOk = false;
      await this.removeDbFiles(collection, filesToRemove);
    }
    const filesToAdd = files.filter(f => !dbFileIds.includes(f.id));
    if (filesToAdd.length > 0) {
      console.log('Files to add:', filesToAdd.length);
      cacheOk = false;
    }

    // Process files in batches of 500
    const batchSize = 500;
    for (let i = 0; i < filesToAdd.length; i += batchSize) {
      const batch = filesToAdd.slice(i, i + batchSize);
      console.log(`Processing batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(filesToAdd.length / batchSize)}`);

      // Read all files in the batch in parallel
      const loadedEntries = await Promise.all(batch.map(async file => {
        const [content, metadata] = await Promise.all([readFile(file.path, file.id, {
          parseText: true
        }), readFileMetadata(file.path, file.id)]);
        return {
          collection,
          fileId: file.id,
          filePath: file.path,
          fileMeta: {
            ...file,
            ...metadata
          },
          fileData: content
        };
      }));

      // Insert entire batch in a single request
      await this.insertDbFilesBatch(loadedEntries);
    }
    if (cacheOk) {
      console.log('Cache is up to date for collection:', collection);
      return;
    }
    return;
  }
}